// The APC services JLI is actually provisioned for (per Jack,
// 2026-08-05: keep the picker to CONFIRMED services only so it doesn't
// get confusing). Anything else APC enable later can be added via
// "Custom service…".
//
// CODES ARE UNPREFIXED. Proven live 2026-08-05 on the Pembroke Dock
// account: PDMP16/PDCP16/PDND16 all rejected ("229 AA / 104 ERROR" =
// unknown service) while plain MP16/CP16/ND16/ROAD booked successfully
// (waybills ...044215-18). The NP/PD "prefixes" from the April email
// thread were display-name changes in the APC portal, not code changes.
//
// Tariff limits from APC's product guide; costs from the Monex rate
// card (01/04/2026 agreement). Multi-consignment pricing confirmed by
// Jack: ND/ROAD bill ONE base rate per consignment regardless of box
// count (additionalParcelCost 0), plus per-kg above the included 20kg —
// e.g. 5 boxes at 15kg total on ND16 = £4.44 flat.
//
// maxPieces 10 on ND/ROAD: the SIGNED contract's rate card (Monex
// agreement 01/04/2026, Schedule 1: "Parcel – Must be boxed – Maximum
// of 10 items per consignment") — the January rate email said 20, the
// contract says 10 and wins. MP/CP are single-item services. Booking
// the wrong service carries a £10 fine per the contract, so these
// backstops are billing-protective, not just tidy.
//
// NOTE: ROAD (Northern Ireland) customs schema was discovered live and
// wired into apcPayload.js (2026-08-05, waybill …044218) — NI can be
// routed in shipping rules once verified end-to-end.

export interface ApcCatalogueService {
    code: string;
    name: string;
    itemType: string;
    maxPieces: number | null;
    maxWeightGrams: number | null;
    maxLengthCm: number | null;
    maxWidthCm: number | null;
    maxHeightCm: number | null;
    baseCost: number | null;
    additionalParcelCost: number | null;
    includedKg: number | null;
    perKgCost: number | null;
    // Human note about what gets ADDED to baseCost at booking time.
    // Zone surcharges are applied per DESTINATION, not per service
    // (apcZones.js on the BE) — baseCost here is always the ZONE 0
    // tariff, so the picker must say so or an admin reads £2.64 and
    // assumes that is what a Highland mailpack costs.
    surchargeNote?: string;
}

export const APC_SERVICE_CATALOGUE: ApcCatalogueService[] = [
    {
        code: "MP16",
        name: "Mail Pack by 16:00",
        itemType: "PACK",
        maxPieces: 1,
        maxWeightGrams: 1000,
        maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: 2.64, additionalParcelCost: null, includedKg: null, perKgCost: null,
        surchargeNote: "+ zone surcharge by destination",
    },
    {
        code: "CP16",
        name: "Courier Pack by 16:00",
        itemType: "PACK",
        maxPieces: 1,
        maxWeightGrams: 5000,
        maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: 3.21, additionalParcelCost: null, includedKg: null, perKgCost: null,
        surchargeNote: "+ zone surcharge by destination",
    },
    {
        code: "ND16",
        name: "Next Day Parcel by 16:00",
        itemType: "PARCEL",
        maxPieces: 10,
        // Per box: "Maximum weight 30 kilos per item" (Monex rate email
        // 29/01/2026 — the contract rate card's "Up to 20kg" column is
        // the PRICING allowance, not the carriage limit; conflating the
        // two has happened twice, see included_kg).
        maxWeightGrams: 30000,
        maxLengthCm: 120, maxWidthCm: 55, maxHeightCm: 50,
        baseCost: 4.44, additionalParcelCost: 0, includedKg: 20, perKgCost: 0.25,
        surchargeNote: "+ zone surcharge by destination",
    },
    {
        code: "ROAD",
        name: "Road Service to Ireland (2-5 day, NI)",
        itemType: "PARCEL",
        maxPieces: 10,
        maxWeightGrams: 30000, // per box — 30kg per item, see ND16 note
        maxLengthCm: 120, maxWidthCm: 55, maxHeightCm: 50,
        // NI pricing (contract: "Tariff charge plus below £11.90 …
        // £0.60/kg"): base is ADDITIVE — £4.44 parcel tariff + £11.90
        // NI = £16.34 (per Jack 2026-08-06). Over 20kg the NI row's
        // OWN escalator applies (£0.60/kg) — the shipment travels on
        // ROAD, not ND16, so the ND £0.25/kg is assumed NOT to stack
        // (Jack checking real invoices to confirm, 2026-08-06).
        baseCost: 16.34, additionalParcelCost: 0, includedKg: 20, perKgCost: 0.60,
        surchargeNote: "NI surcharge already included in the base cost",
    },
    {
        code: "TDAY",
        name: "2-5 Day Service",
        itemType: "PARCEL",
        maxPieces: 10,
        maxWeightGrams: 30000, // per box — 30kg per item, see ND16 note
        maxLengthCm: 120, maxWidthCm: 55, maxHeightCm: 50,
        // Scottish Highlands & Islands. Unlike ROAD (whose NI surcharge is
        // baked into baseCost because apcZones has no BT entry), Scottish
        // destinations DO carry zone entries, so this stays at the Zone 0
        // parcel tariff and the surcharge is added at booking.
        baseCost: 4.44, additionalParcelCost: 0, includedKg: 20, perKgCost: 0.25,
        surchargeNote: "+ Zone 13 £9.80 or Zone 8 £16.00 by destination",
    },
    {
        code: "TDMP",
        name: "2-5 Day Mail Pack",
        itemType: "PACK",
        maxPieces: 1,
        maxWeightGrams: 1000,
        maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: 2.64, additionalParcelCost: null, includedKg: null, perKgCost: null,
        surchargeNote: "+ Zone 13 £9.80 or Zone 8 £16.00 by destination",
    },
    {
        code: "TDCP",
        name: "2-5 Day Courier Pack",
        itemType: "PACK",
        maxPieces: 1,
        maxWeightGrams: 5000,
        maxLengthCm: null, maxWidthCm: null, maxHeightCm: null,
        baseCost: 3.21, additionalParcelCost: null, includedKg: null, perKgCost: null,
        surchargeNote: "+ Zone 13 £9.80 or Zone 8 £16.00 by destination",
    },
];

// Codes are account-scoped, not depot-prefixed (proven live — see the
// header). Kept as a function so a per-warehouse prefix can return if
// APC ever provision one; today every account uses the bare code.
export const depotPrefixFor = (_warehouse: string): string => "";
